/** Urgent notices (danger, lost life) show at once; info notices (shift change, extra life) wait their turn. */
export const ToastPriority = {
  Info: 'INFO',
  Urgent: 'URGENT',
} as const;
export type ToastPriority = (typeof ToastPriority)[keyof typeof ToastPriority];

interface Toast {
  readonly text: string;
  readonly seconds: number;
  readonly priority: ToastPriority;
}

/**
 * Order of the HUD notices – the HUD has room for one at a time. An urgent notice replaces the
 * current one; an info notice it pushes aside comes back afterwards. Info notices queue up behind
 * whatever is showing. Between two notices the toast stays hidden for `gap` seconds so the change
 * reads as a new notice (fade out, fade in).
 */
export class ToastQueue {
  private current: Toast | null = null;
  private remaining = 0;
  private gapLeft = 0;
  private readonly pending: Toast[] = [];

  constructor(
    private readonly gap: number,
    private readonly maxPending: number,
  ) {}

  /** Text to show now, or null while hidden. */
  get text(): string | null {
    return this.current && this.gapLeft <= 0 ? this.current.text : null;
  }

  push(text: string, seconds: number, priority: ToastPriority): void {
    const toast: Toast = { text, seconds, priority };
    if (priority === ToastPriority.Urgent) {
      const pushedAside = this.current;
      if (pushedAside?.priority === ToastPriority.Info && pushedAside.text !== text) this.pending.unshift(pushedAside);
      this.show(toast, 0);
      this.trim();
      return;
    }
    if (this.current?.text === text || this.pending.some((p) => p.text === text)) return;
    if (!this.current) this.show(toast, 0);
    else {
      this.pending.push(toast);
      this.trim();
    }
  }

  update(dt: number): void {
    if (!this.current) return;
    if (this.gapLeft > 0) {
      this.gapLeft -= dt;
      return;
    }
    this.remaining -= dt;
    if (this.remaining > 0) return;
    const next = this.pending.shift();
    if (next) this.show(next, this.gap);
    else this.current = null;
  }

  clear(): void {
    this.current = null;
    this.remaining = 0;
    this.gapLeft = 0;
    this.pending.length = 0;
  }

  private show(toast: Toast, gap: number): void {
    this.current = toast;
    this.remaining = toast.seconds;
    this.gapLeft = gap;
  }

  private trim(): void {
    while (this.pending.length > this.maxPending) this.pending.pop();
  }
}
